package com.devsusana.portfolio.components.layouts

import androidx.compose.runtime.*
import com.devsusana.portfolio.components.widgets.LanguageToggle
import com.devsusana.portfolio.components.widgets.SocialLinks
import com.devsusana.portfolio.components.widgets.ThemeToggle
import com.devsusana.portfolio.i18n.rememberStrings
import com.devsusana.portfolio.styles.SitePalette
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh

/**
 * Global PageLayout component acting as a Web Scaffold.
 * Includes a top navigation header, responsive content slot, and footer.
 */
@Composable
fun PageLayout(
    title: String = "Susana Córdoba | Senior Android Engineer & Tech Lead",
    content: @Composable () -> Unit
) {
    LaunchedEffect(title) {
        document.title = title
    }

    val colorMode = ColorMode.current

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .minHeight(100.vh)
            .backgroundColor(SitePalette.background(colorMode)),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Navigation Header
        NavigationBar()

        // Main Content Area
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(1120.px)
                .styleModifier {
                    property("padding-left", "clamp(0.75rem, 3vw, 1.5rem)")
                    property("padding-right", "clamp(0.75rem, 3vw, 1.5rem)")
                    property("padding-top", "1.5rem")
                    property("padding-bottom", "2rem")
                },
            contentAlignment = Alignment.TopCenter
        ) {
            content()
        }

        Spacer()

        // Footer
        Footer()
    }
}

@Composable
private fun NavigationBar() {
    val colorMode = ColorMode.current
    val strings = rememberStrings()

    var activeSection by remember { mutableStateOf("home") }
    var isMobileMenuOpen by remember { mutableStateOf(false) }
    var isMobile by remember { mutableStateOf(false) }

    DisposableEffect(Unit) {
        val checkDimensions: () -> Unit = {
            try {
                val width = js("window.innerWidth || document.documentElement.clientWidth || 0") as Int
                isMobile = width < 860
            } catch (_: Throwable) {}
        }
        checkDimensions()

        val onScroll: (dynamic) -> Unit = {
            try {
                val scrollY = js("window.scrollY || window.pageYOffset || 0") as Double
                val windowHeight = js("window.innerHeight || document.documentElement.clientHeight || 0") as Double
                val docHeight = js("document.documentElement.scrollHeight || document.body.scrollHeight || 0") as Double

                if (scrollY + windowHeight >= docHeight - 80.0) {
                    activeSection = "blog"
                } else if (scrollY < 200.0) {
                    activeSection = "home"
                } else {
                    val sections = listOf("blog", "experience", "agentic", "hometutorpro")
                    var matched = false
                    for (id in sections) {
                        val el = kotlinx.browser.document.getElementById(id)
                        if (el != null) {
                            val rectTop = el.getBoundingClientRect().top
                            if (rectTop <= 220.0) {
                                activeSection = id
                                matched = true
                                break
                            }
                        }
                    }
                    if (!matched) {
                        activeSection = "home"
                    }
                }
            } catch (_: Throwable) {}
        }

        val onResize: (dynamic) -> Unit = {
            checkDimensions()
        }

        try {
            kotlinx.browser.window.addEventListener("scroll", onScroll)
            kotlinx.browser.window.addEventListener("resize", onResize)
            onScroll(null)
        } catch (_: Throwable) {}

        onDispose {
            try {
                kotlinx.browser.window.removeEventListener("scroll", onScroll)
                kotlinx.browser.window.removeEventListener("resize", onResize)
            } catch (_: Throwable) {}
        }
    }

    val navBg = when (colorMode) {
        ColorMode.LIGHT -> com.varabyte.kobweb.compose.ui.graphics.Color.rgba(255, 255, 255, 0.88f)
        ColorMode.DARK -> com.varabyte.kobweb.compose.ui.graphics.Color.rgba(30, 41, 59, 0.88f)
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .position(org.jetbrains.compose.web.css.Position.Sticky)
            .top(0.px)
            .zIndex(100)
            .backgroundColor(navBg)
            .styleModifier {
                property("backdrop-filter", "blur(12px)")
                property("-webkit-backdrop-filter", "blur(12px)")
                property("padding-left", "clamp(0.75rem, 2.5vw, 1.5rem)")
                property("padding-right", "clamp(0.75rem, 2.5vw, 1.5rem)")
                property("padding-top", "0.75rem")
                property("padding-bottom", "0.75rem")
            }
            .borderBottom(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SitePalette.surfaceBorder(colorMode)),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(1120.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Brand Logo / Home link with smooth scroll to top
                Link(
                    path = "/#home",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = Modifier
                        .cursor(Cursor.Pointer)
                        .onClick {
                            try {
                                if (kotlinx.browser.window.location.pathname != "/") {
                                    kotlinx.browser.window.location.href = "/#home"
                                } else {
                                    js("window.scrollTo({ top: 0, behavior: 'smooth' })")
                                }
                            } catch (_: Throwable) {}
                            activeSection = "home"
                            isMobileMenuOpen = false
                        }
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(0.5.cssRem)
                    ) {
                        Box(
                            modifier = Modifier
                                .size(34.px)
                                .borderRadius(8.px)
                                .backgroundColor(SitePalette.androidGreen),
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                text = "S",
                                modifier = Modifier
                                    .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x0F, 0x17, 0x2A))
                                    .fontWeight(FontWeight.Bold)
                                    .fontSize(1.1.cssRem)
                            )
                        }
                        Column {
                            SpanText(
                                text = "Susana Córdoba",
                                modifier = Modifier
                                    .color(SitePalette.textPrimary(colorMode))
                                    .fontWeight(FontWeight.Bold)
                                    .fontSize(1.cssRem)
                            )
                            SpanText(
                                text = strings.navRole,
                                modifier = Modifier
                                    .color(SitePalette.textSecondary(colorMode))
                                    .fontSize(0.72.cssRem)
                            )
                        }
                    }
                }

                if (!isMobile) {
                    // Desktop Navigation Links
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(0.85.cssRem),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        NavLinkItem(
                            label = strings.navHome,
                            path = "/#home",
                            isActive = activeSection == "home" && kotlinx.browser.window.location.pathname == "/",
                            onClick = {
                                try {
                                    if (kotlinx.browser.window.location.pathname != "/") {
                                        kotlinx.browser.window.location.href = "/#home"
                                    } else {
                                        js("window.scrollTo({ top: 0, behavior: 'smooth' })")
                                    }
                                } catch (_: Throwable) {}
                                activeSection = "home"
                            }
                        )
                        NavLinkItem(
                            label = strings.navHomeTutorPro,
                            path = "/#hometutorpro",
                            isActive = activeSection == "hometutorpro",
                            onClick = {
                                try {
                                    if (kotlinx.browser.window.location.pathname != "/") {
                                        kotlinx.browser.window.location.href = "/#hometutorpro"
                                    } else {
                                        js("var el = document.getElementById('hometutorpro'); if (el) el.scrollIntoView({ behavior: 'smooth' });")
                                    }
                                } catch (_: Throwable) {}
                                activeSection = "hometutorpro"
                            }
                        )
                        NavLinkItem(
                            label = strings.navAgentic,
                            path = "/#agentic",
                            isActive = activeSection == "agentic",
                            onClick = {
                                try {
                                    if (kotlinx.browser.window.location.pathname != "/") {
                                        kotlinx.browser.window.location.href = "/#agentic"
                                    } else {
                                        js("var el = document.getElementById('agentic'); if (el) el.scrollIntoView({ behavior: 'smooth' });")
                                    }
                                } catch (_: Throwable) {}
                                activeSection = "agentic"
                            }
                        )
                        NavLinkItem(
                            label = strings.navExperience,
                            path = "/#experience",
                            isActive = activeSection == "experience",
                            onClick = {
                                try {
                                    if (kotlinx.browser.window.location.pathname != "/") {
                                        kotlinx.browser.window.location.href = "/#experience"
                                    } else {
                                        js("var el = document.getElementById('experience'); if (el) el.scrollIntoView({ behavior: 'smooth' });")
                                    }
                                } catch (_: Throwable) {}
                                activeSection = "experience"
                            }
                        )
                        NavLinkItem(
                            label = strings.navBlog,
                            path = "/#blog",
                            isActive = activeSection == "blog" || kotlinx.browser.window.location.pathname.startsWith("/posts"),
                            onClick = {
                                try {
                                    if (kotlinx.browser.window.location.pathname != "/") {
                                        kotlinx.browser.window.location.href = "/#blog"
                                    } else {
                                        js("var el = document.getElementById('blog'); if (el) el.scrollIntoView({ behavior: 'smooth' });")
                                    }
                                } catch (_: Throwable) {}
                                activeSection = "blog"
                            }
                        )

                        LanguageToggle()
                        ThemeToggle()
                    }
                } else {
                    // Mobile controls (Language + Theme + Hamburger Toggle)
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(0.5.cssRem),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        LanguageToggle()
                        ThemeToggle()
                        Box(
                            modifier = Modifier
                                .size(36.px)
                                .borderRadius(8.px)
                                .backgroundColor(
                                    if (isMobileMenuOpen) SitePalette.androidGreen
                                    else if (colorMode.isDark) com.varabyte.kobweb.compose.ui.graphics.Color.rgba(255, 255, 255, 0.08f)
                                    else com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0.06f)
                                )
                                .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SitePalette.surfaceBorder(colorMode))
                                .cursor(Cursor.Pointer)
                                .onClick { isMobileMenuOpen = !isMobileMenuOpen },
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                text = if (isMobileMenuOpen) "✕" else "☰",
                                modifier = Modifier
                                    .fontSize(1.15.cssRem)
                                    .fontWeight(FontWeight.Bold)
                                    .color(
                                        if (isMobileMenuOpen) com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x0F, 0x17, 0x2A)
                                        else SitePalette.textPrimary(colorMode)
                                    )
                            )
                        }
                    }
                }
            }

            // Mobile menu drawer
            if (isMobile && isMobileMenuOpen) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 0.75.cssRem)
                        .backgroundColor(SitePalette.surface(colorMode))
                        .borderRadius(12.px)
                        .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SitePalette.surfaceBorder(colorMode))
                        .padding(0.5.cssRem)
                        .styleModifier {
                            property("box-shadow", "0 10px 25px -5px rgba(0, 0, 0, 0.3)")
                        },
                    verticalArrangement = Arrangement.spacedBy(0.25.cssRem)
                ) {
                    MobileNavLinkItem(
                        label = strings.navHome,
                        path = "/#home",
                        isActive = activeSection == "home" && kotlinx.browser.window.location.pathname == "/",
                        onClick = {
                            isMobileMenuOpen = false
                            try {
                                if (kotlinx.browser.window.location.pathname != "/") {
                                    kotlinx.browser.window.location.href = "/#home"
                                } else {
                                    js("window.scrollTo({ top: 0, behavior: 'smooth' })")
                                }
                            } catch (_: Throwable) {}
                            activeSection = "home"
                        }
                    )
                    MobileNavLinkItem(
                        label = strings.navHomeTutorPro,
                        path = "/#hometutorpro",
                        isActive = activeSection == "hometutorpro",
                        onClick = {
                            isMobileMenuOpen = false
                            try {
                                if (kotlinx.browser.window.location.pathname != "/") {
                                    kotlinx.browser.window.location.href = "/#hometutorpro"
                                } else {
                                    js("var el = document.getElementById('hometutorpro'); if (el) el.scrollIntoView({ behavior: 'smooth' });")
                                }
                            } catch (_: Throwable) {}
                            activeSection = "hometutorpro"
                        }
                    )
                    MobileNavLinkItem(
                        label = strings.navAgentic,
                        path = "/#agentic",
                        isActive = activeSection == "agentic",
                        onClick = {
                            isMobileMenuOpen = false
                            try {
                                if (kotlinx.browser.window.location.pathname != "/") {
                                    kotlinx.browser.window.location.href = "/#agentic"
                                } else {
                                    js("var el = document.getElementById('agentic'); if (el) el.scrollIntoView({ behavior: 'smooth' });")
                                }
                            } catch (_: Throwable) {}
                            activeSection = "agentic"
                        }
                    )
                    MobileNavLinkItem(
                        label = strings.navExperience,
                        path = "/#experience",
                        isActive = activeSection == "experience",
                        onClick = {
                            isMobileMenuOpen = false
                            try {
                                if (kotlinx.browser.window.location.pathname != "/") {
                                    kotlinx.browser.window.location.href = "/#experience"
                                } else {
                                    js("var el = document.getElementById('experience'); if (el) el.scrollIntoView({ behavior: 'smooth' });")
                                }
                            } catch (_: Throwable) {}
                            activeSection = "experience"
                        }
                    )
                    MobileNavLinkItem(
                        label = strings.navBlog,
                        path = "/#blog",
                        isActive = activeSection == "blog" || kotlinx.browser.window.location.pathname.startsWith("/posts"),
                        onClick = {
                            isMobileMenuOpen = false
                            try {
                                if (kotlinx.browser.window.location.pathname != "/") {
                                    kotlinx.browser.window.location.href = "/#blog"
                                } else {
                                    js("var el = document.getElementById('blog'); if (el) el.scrollIntoView({ behavior: 'smooth' });")
                                }
                            } catch (_: Throwable) {}
                            activeSection = "blog"
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun NavLinkItem(
    label: String,
    path: String,
    isActive: Boolean,
    onClick: () -> Unit
) {
    val colorMode = ColorMode.current
    Link(
        path = path,
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
    ) {
        Box(
            modifier = Modifier
                .borderRadius(6.px)
                .backgroundColor(
                    if (isActive) {
                        if (colorMode.isDark) com.varabyte.kobweb.compose.ui.graphics.Color.rgba(61, 220, 132, 0.15f)
                        else com.varabyte.kobweb.compose.ui.graphics.Color.rgba(61, 220, 132, 0.15f)
                    } else {
                        com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0f)
                    }
                )
                .padding(leftRight = 10.px, topBottom = 6.px),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                text = label,
                modifier = Modifier
                    .color(
                        if (isActive) SitePalette.androidGreen
                        else SitePalette.textSecondary(colorMode)
                    )
                    .fontSize(0.95.cssRem)
                    .fontWeight(if (isActive) FontWeight.Bold else FontWeight.Medium)
            )
        }
    }
}

@Composable
private fun MobileNavLinkItem(
    label: String,
    path: String,
    isActive: Boolean,
    onClick: () -> Unit
) {
    val colorMode = ColorMode.current
    Link(
        path = path,
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
        modifier = Modifier
            .fillMaxWidth()
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .borderRadius(8.px)
                .backgroundColor(
                    if (isActive) {
                        if (colorMode.isDark) com.varabyte.kobweb.compose.ui.graphics.Color.rgba(61, 220, 132, 0.15f)
                        else com.varabyte.kobweb.compose.ui.graphics.Color.rgba(61, 220, 132, 0.15f)
                    } else {
                        com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0f)
                    }
                )
                .padding(leftRight = 1.cssRem, topBottom = 0.75.cssRem),
            contentAlignment = Alignment.CenterStart
        ) {
            SpanText(
                text = label,
                modifier = Modifier
                    .color(
                        if (isActive) SitePalette.androidGreen
                        else SitePalette.textPrimary(colorMode)
                    )
                    .fontSize(1.cssRem)
                    .fontWeight(if (isActive) FontWeight.Bold else FontWeight.Medium)
            )
        }
    }
}

@Composable
private fun Footer() {
    val colorMode = ColorMode.current
    val strings = rememberStrings()

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(SitePalette.surface(colorMode))
            .borderTop(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SitePalette.surfaceBorder(colorMode))
            .padding(topBottom = 2.cssRem, leftRight = 1.5.cssRem),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(1120.px),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(1.cssRem)
        ) {
            SocialLinks()

            SpanText(
                text = strings.footerCopyright,
                modifier = Modifier
                    .color(SitePalette.textSecondary(colorMode))
                    .fontSize(0.85.cssRem)
            )
        }
    }
}
